// Notifications.js - Ativar/desativar notificações neste dispositivo
//
// A subscrição é POR DISPOSITIVO, não por conta: o telemóvel, o tablet e
// o portátil têm cada um o seu endpoint de push. Por isso este ecrã fala
// sempre de "este dispositivo" e mostra quantos outros estão ligados.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Bell, BellOff, Smartphone, CheckCircle2, AlertTriangle, Send,
} from 'lucide-react';
import {
  podeSubscrever, motivoIndisponivel, estadoDaSubscricao,
  subscrever, cancelarSubscricao, enviarTeste,
} from './pushNotifications';
import './Notifications.css';

function Notifications({ apiUrl, onBack, token }) {
  const { t } = useTranslation();
  const [estado, setEstado] = useState(null);
  const [dispositivos, setDispositivos] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');

  const disponivel = podeSubscrever();

  const carregar = useCallback(async () => {
    setEstado(await estadoDaSubscricao());
    try {
      const r = await fetch(`${apiUrl}/push/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setDispositivos((await r.json()).devices || 0);
    } catch {
      /* o número de dispositivos é acessório - não vale um erro no ecrã */
    }
  }, [apiUrl, token]);

  useEffect(() => { carregar(); }, [carregar]);

  const alternar = async () => {
    setOcupado(true);
    setErro('');
    setAviso('');

    const r = estado?.subscrito ? await cancelarSubscricao() : await subscrever();
    if (!r.ok) {
      setErro(r.erro);
    } else {
      setAviso(estado?.subscrito
        ? t('notificacoes.deixouDeReceber')
        : t('notificacoes.passouAReceber'));
    }

    await carregar();
    setOcupado(false);
  };

  const testar = async () => {
    setOcupado(true);
    setErro('');
    setAviso('');
    const r = await enviarTeste();
    if (r.ok) {
      setAviso(t('notificacoes.testeEnviado', { count: r.enviadas }));
    } else {
      setErro(r.erro || t('notificacoes.naoFoiPossivelEnviar'));
    }
    setOcupado(false);
  };

  return (
    <div className="nt-view">
      <div className="nt-header">
        <button onClick={onBack} className="nt-back"><ArrowLeft size={22} /></button>
        <div>
          <h1><Bell size={26} /> {t('notificacoes.titulo')}</h1>
          <p>{t('notificacoes.subtitulo')}</p>
        </div>
      </div>

      {/* Estado deste dispositivo */}
      <div className={`nt-card ${estado?.subscrito ? 'on' : ''}`}>
        <div className="nt-card-icon">
          {estado?.subscrito ? <Bell size={26} /> : <BellOff size={26} />}
        </div>
        <div className="nt-card-body">
          <h2>{estado?.subscrito ? t('notificacoes.ativas') : t('notificacoes.desativadas')}</h2>
          <p>
            {estado?.subscrito
              ? t('notificacoes.descricaoAtivas')
              : t('notificacoes.descricaoDesativadas')}
          </p>
        </div>
      </div>

      {!disponivel ? (
        <div className="nt-help">
          <Smartphone size={20} />
          <p>{motivoIndisponivel()}</p>
        </div>
      ) : (
        <div className="nt-actions">
          <button className={`nt-btn ${estado?.subscrito ? 'off' : 'on'}`} onClick={alternar} disabled={ocupado}>
            {estado?.subscrito ? <><BellOff size={18} /> {t('notificacoes.desativar')}</> : <><Bell size={18} /> {t('notificacoes.ativar')}</>}
          </button>
          {estado?.subscrito && (
            <button className="nt-btn ghost" onClick={testar} disabled={ocupado}>
              <Send size={18} /> {t('notificacoes.enviarTeste')}
            </button>
          )}
        </div>
      )}

      {aviso && <div className="nt-msg ok"><CheckCircle2 size={18} /> {aviso}</div>}
      {erro && <div className="nt-msg err"><AlertTriangle size={18} /> {erro}</div>}

      {dispositivos > 0 && (
        <p className="nt-count">
          {dispositivos === 1
            ? t('notificacoes.umDispositivo')
            : t('notificacoes.variosDispositivos', { count: dispositivos })}
        </p>
      )}

      <div className="nt-what">
        <h3>{t('notificacoes.oQueNotifica')}</h3>
        <ul>
          <li><b>{t('notificacoes.itemPrograma')}</b> — {t('notificacoes.itemProgramaDesc')}</li>
          <li><b>{t('notificacoes.itemAvisos')}</b> — {t('notificacoes.itemAvisosDesc')}</li>
          <li><b>{t('notificacoes.itemProblemas')}</b> — {t('notificacoes.itemProblemasDesc')}</li>
        </ul>
        <p>
          {t('notificacoes.rodape')}
        </p>
      </div>
    </div>
  );
}

export default Notifications;
